import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import type { StaffAccountStatus } from '../staff/model'
import { Fromable, GENDER } from '../model'

@Serializable()
export class Address extends Fromable {
  @JsonProperty('id')
  id?: number

  @JsonProperty('type')
  type!: string

  @JsonProperty('addressLine')
  addressLine!: string

  @JsonProperty('subDistrictId')
  subDistrictId!: number

  @JsonProperty('districtId')
  districtId!: number

  @JsonProperty('provinceId')
  provinceId!: number

  @JsonProperty('postalCode')
  postalCode!: string

  @JsonProperty('firstName')
  firstName?: any

  @JsonProperty('lastName')
  lastName?: any

  @JsonProperty('companyName')
  companyName?: any

  @JsonProperty('branch')
  branch?: any

  @JsonProperty('taxNo')
  taxNo?: any

  public serialize() {
    return this
  }
}

@Serializable()
export class Role extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code?: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('group')
  group!: string

  @JsonProperty('position')
  position!: string

  public serialize() {
    return this
  }
}

@Serializable()
export class EmergencyContact extends Fromable {
  @JsonProperty('userId')
  userId?: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('relationship')
  relationship!: string

  public serialize() {
    return this
  }
}

@Serializable()
export class ReferralSource extends Fromable {
  @JsonProperty('userId')
  userId?: number

  @JsonProperty('source')
  source!: string

  @JsonProperty('referralChannelOther')
  referralChannelOther?: string

  public serialize() {
    return this
  }
}

@Serializable()
export class TrainerServiceGroup extends Fromable {
  @JsonProperty('groupId')
  groupId!: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string
}

@Serializable()
export class TrainerUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('roleId')
  roleId!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('contractType')
  contractType!: string

  @JsonProperty({
    path: 'serviceGroup',
    elementType: TrainerServiceGroup,
  })
  serviceGroup!: TrainerServiceGroup[]

  /**
   * QTY-2042: staff soft-delete status. Present on the management lists, which
   * ask for every status; pickers filter to ACTIVE and never see one.
   */
  @JsonProperty({ path: 'accountStatus', required: false })
  accountStatus?: StaffAccountStatus

  public getOption() {
    return {
      label: `${this.firstName} ${this.lastName}`,
      value: this.id,
    }
  }

  public fullName() {
    return `${this.firstName} ${this.lastName}`
  }
}

@Serializable()
export class Trainer extends Fromable {
  @JsonProperty('userId')
  userId?: number

  @JsonProperty('videoUrl')
  videoUrl!: string

  @JsonProperty('type')
  type!: string

  @JsonProperty('specialties')
  specialties!: string[]

  @JsonProperty('certificates')
  certificates!: string[]

  @JsonProperty('isPersonalTraining')
  isPersonalTraining!: boolean

  @JsonProperty('level')
  level!: string

  @JsonProperty('bookingLeadTimeMinutes')
  bookingLeadTimeMinutes?: number | null

  @JsonProperty('imageUrl')
  imageUrl?: string

  public serializeForCreate() {
    return { ...this }
  }
}

@Serializable()
export class ServiceData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('groupId')
  groupId!: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('description')
  description!: string

  @DayjsProperty()
  createdAt!: Dayjs
}

@Serializable()
export class TrainerService extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('trainerId')
  trainerId!: number

  @JsonProperty('serviceId')
  serviceId!: number

  @JsonProperty('price')
  price!: number

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @JsonProperty({
    path: 'service',
    type: ServiceData,
  })
  service!: ServiceData

  public serializeForCreate() {
    return {
      serviceId: this.serviceId,
      price: this.price,
    }
  }
}

@Serializable()
export class UserMembershipPackageClass extends Fromable {
  @JsonProperty('classId')
  classId!: number
}

@Serializable()
export class UserMembershipPackage extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name?: string

  @JsonProperty('packageTypeId')
  packageTypeId!: number

  @JsonProperty('accessType')
  accessType!: string

  @JsonProperty('quotas')
  quotas?: any[]

  @JsonProperty({
    path: 'packageClass',
    elementType: UserMembershipPackageClass,
  })
  packageClass!: UserMembershipPackageClass[]
}

@Serializable()
export class MembershipTrainer extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string
}

@Serializable()
export class UserMembershipData extends Fromable {
  @JsonProperty('id')
  id?: number

  @JsonProperty('userId')
  userId!: number

  @JsonProperty('expireDate')
  expireDate!: string

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('package')
  package!: UserMembershipPackage

  @JsonProperty('remainingUses')
  remainingUses!: number

  @JsonProperty({ path: 'trainer', type: MembershipTrainer })
  trainer?: MembershipTrainer | null

  @JsonProperty('quotas')
  quotas?: any[]
}

@Serializable()
export class PersonalMemberInfo extends Fromable {
  @JsonProperty('weight')
  weight!: number

  @JsonProperty('height')
  height!: number

  @JsonProperty('bloodType')
  bloodType!: string

  @JsonProperty('marriedStatus')
  marriedStatus!: string

  @JsonProperty('saleId')
  saleId!: number

  @JsonProperty('note')
  note?: string

  /**
   * Allergies / product sensitivities, surfaced as a warning wherever a treatment is about to be
   * performed (Wellness & Spa). Read from here as a fallback if the BE nests it with the rest of
   * the member profile rather than on the user root.
   */
  @JsonProperty('allergyNote')
  allergyNote?: string | null

  /**
   * The therapist this member asks for (`personal_member_info.preferred_therapist_user_id`). The
   * spa booking modal preselects them whenever they are free for the chosen slot.
   */
  @JsonProperty('preferredTherapistUserId')
  preferredTherapistUserId?: number | null

  /** Resolved display name for the above, so a read-only view needs no staff lookup. */
  @JsonProperty('preferredTherapistName')
  preferredTherapistName?: string | null

  @JsonProperty('credit')
  credit!: number

  @JsonProperty('status')
  status!: string

  @JsonProperty('memberType')
  memberType!: string
}

@Serializable()
export class CreditBalanceData extends Fromable {
  @JsonProperty('totalCredits')
  totalCredits!: number

  @JsonProperty('updatedAt')
  updatedAt!: string

  @JsonProperty('userId')
  userId!: number
}

@Serializable()
export class WorkScheduleItem extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('userId')
  userId!: number

  @DayjsProperty()
  @JsonProperty('startTime')
  startTime!: Dayjs

  @DayjsProperty()
  @JsonProperty('endTime')
  endTime!: Dayjs

  @JsonProperty('dayOfWeek')
  dayOfWeek!: string
}

@Serializable()
export class ServiceGroup extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string
}

@Serializable()
export class TrainingService extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty({
    path: 'serviceGroup',
    type: ServiceGroup,
  })
  serviceGroup!: ServiceGroup

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('nameEn')
  nameEn!: string

  @DayjsProperty()
  createdAt!: Dayjs

  @JsonProperty('description')
  description!: string
}

@Serializable()
export class TrainingServiceItem extends Fromable {
  @JsonProperty('id')
  id?: number

  @JsonProperty('trainerId')
  trainerId?: number

  @JsonProperty('serviceId')
  serviceId?: number

  @JsonProperty('price')
  price!: number

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @JsonProperty({ path: 'trainingService', type: TrainingService })
  service!: TrainingService
}

@Serializable()
export class User extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firebaseUid')
  firebaseUid?: string

  @JsonProperty('code')
  code!: string

  @JsonProperty('roleId')
  roleId!: string

  @JsonProperty({ path: 'role', type: Role })
  role?: Role

  @JsonProperty('presetCodes')
  presetCodes?: string[]

  @JsonProperty('imageUrl')
  imageUrl!: string

  @JsonProperty('citizenId')
  citizenId!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: GENDER

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('altEmail')
  altEmail!: string

  @JsonProperty('altPhone')
  altPhone!: string

  @JsonProperty('occupation')
  occupation!: string

  @JsonProperty('position')
  position!: string

  @JsonProperty('lineId')
  lineId!: string

  @JsonProperty('facebook')
  facebook!: string

  @JsonProperty('instagram')
  instagram!: string

  @DayjsProperty()
  dateOfBirth!: string

  @DayjsProperty()
  banUntil!: Dayjs

  @JsonProperty('accountStatus')
  accountStatus?: string

  @JsonProperty('hasActivePackage')
  hasActivePackage?: boolean

  @JsonProperty('memberStatus')
  memberStatus?: string

  @JsonProperty('packageStatus')
  packageStatus?: string

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @DayjsProperty()
  deletedAt!: Dayjs

  @JsonProperty({
    path: 'address',
    elementType: Address,
  })
  address!: Address[]

  @JsonProperty({
    path: 'emergencyContact',
    type: EmergencyContact,
  })
  emergencyContact!: EmergencyContact

  @JsonProperty({
    path: 'referralSource',
    type: ReferralSource,
  })
  referralSource!: ReferralSource

  @JsonProperty({
    path: 'trainerProfile',
    type: Trainer,
  })
  trainerProfile!: Trainer

  @JsonProperty('personalMemberInfo')
  personalMemberInfo?: PersonalMemberInfo

  /** Allergies / product sensitivities (Wellness & Spa). Null for members who have none. */
  @JsonProperty('allergyNote')
  allergyNote?: string | null

  /**
   * Preferred spa therapist (Wellness & Spa v1.1). Mirrored on the user root as well as inside
   * `personalMemberInfo` for the same reason `allergyNote` is: read whichever the BE fills.
   */
  @JsonProperty('preferredTherapistUserId')
  preferredTherapistUserId?: number | null

  @JsonProperty('preferredTherapistName')
  preferredTherapistName?: string | null

  @JsonProperty('creditBalance')
  creditBalance?: CreditBalanceData

  @JsonProperty('userMembership')
  userMembership?: UserMembershipData[]

  @JsonProperty({
    path: 'trainerService',
    elementType: TrainingServiceItem,
  })
  trainerService!: TrainingServiceItem[]

  @JsonProperty('workSchedule')
  workSchedule?: WorkScheduleItem[]

  @JsonProperty('pdpaESignatureDataId')
  pdpaESignatureDataId?: string | null

  public fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  public option() {
    return {
      label: `${this.code} ${this.firstName} ${this.lastName}`,
      value: this.id,
    }
  }
}
